/**
 * Pure types for CGStep components.
 * These types are platform-agnostic and match the server types without importing them.
 */

// ============================================================================
// Step State & Type Enums
// ============================================================================

export type CadenceStepStatePure =
  | 'not-started'
  | 'interrupted'
  | 'skipped'
  | 'finished'
  | 'canceled'
  | 'action-required'
  | 'responded'
  | 'contact_unavailable';

export type CadenceStepTypePure =
  | 'message'
  | 'connect'
  | 'inmail'
  | 'visit'
  | 'follow'
  | 'email'
  | 'reminder'
  | 'call'
  | 'delay'
  | 'likeRecentPosts'
  | 'wait_for_connect'
  | 'voice_note'
  | 'sift'
  | 'switch_campaign';

// ============================================================================
// Sift Configuration
// ============================================================================

export type SiftConditionPure = {
  id?: string;
  field: string;
  operator: string;
  value?: string;
};

export type SiftConfigPure = {
  conditions: SiftConditionPure[];
};

// ============================================================================
// Template Step (for diff comparison)
// ============================================================================

export type TemplateStepPure = {
  type?: CadenceStepTypePure;
  body2?: string | null;
  subject?: string | null;
  postsToLike?: number | null;
  onlyOpenLink?: boolean | null;
  cc?: string | null;
  bcc?: string | null;
  delay?: number;
};

// ============================================================================
// Cadence Step Node
// ============================================================================

export type CadenceStepNodePure = {
  id: string;
  type: CadenceStepTypePure;
  state: CadenceStepStatePure;
  delay: number;
  canRunOn?: Date | null;
  completedOn?: Date | null;
  errorMessage?: string | null;
  /** Reason code for a `skipped` step (e.g. `follow_rejected`). Surfaced as a muted row sub-line. */
  skipReason?: string | null;
  body2?: string | null;
  subject?: string | null;
  cc?: string | null;
  bcc?: string | null;
  postsToLike?: number | null;
  onlyOpenLink?: boolean | null;
  siftConditions?: SiftConfigPure | null;
  templateStepId?: string | null;
  templateStep?: TemplateStepPure | null;
  computedError?: {
    default?: string;
    body2?: string;
    subject?: string;
  };
  selectedOption?: 'one' | 'two' | null;
  next: {
    one?: string | null;
    two?: string | null;
  };
  group: string[];
};

// ============================================================================
// Graph Types
// ============================================================================

export type CadenceGraphNodesPure = Record<string, CadenceStepNodePure>;

// ============================================================================
// User Team Data (minimal for CGStep)
// ============================================================================

export type CGStepUserTeamPure = {
  hasSalesNav?: boolean | null;
};

// ============================================================================
// Document Data
// ============================================================================

export type CGStepDocumentPure = {
  id: string;
  title?: string | null;
  url?: string | null;
  ogImageUrl?: string | null;
};

// ============================================================================
// Different from Template (for highlighting changes)
// ============================================================================

export type DifferentFromTemplatePure = {
  type?: boolean;
  postsToLike?: boolean;
  body2?: boolean;
  subject?: boolean;
  cc?: boolean;
  bcc?: boolean;
  onlyOpenLink?: boolean;
};

// ============================================================================
// Step Input Types
// ============================================================================

export type StepCreateInputPure = {
  type: string;
  body2?: string | null;
  subject?: string | null;
  delay?: number;
  afterStepId?: string | null;
  option?: 'one' | 'two';
  cc?: string;
  bcc?: string;
  onlyOpenLink?: boolean;
  postsToLike?: number;
};

export type StepUpdateInputPure = {
  type?: string;
  body2?: string | null;
  subject?: string | null;
  delay?: number;
  onlyOpenLink?: boolean;
  postsToLike?: number | null;
  cc?: string | null;
  bcc?: string | null;
  siftConditions?: SiftConfigPure | null;
};

// ============================================================================
// Sift Field Options (for display)
// ============================================================================

export const SIFT_FIELD_OPTIONS_PURE = [
  { field: 'firstName', label: 'First name' },
  { field: 'lastName', label: 'Last name' },
  { field: 'title', label: 'Job title' },
  { field: 'company', label: 'Company' },
  { field: 'industry', label: 'Industry' },
  { field: 'city', label: 'City' },
  { field: 'headline', label: 'Headline' },
  { field: 'about', label: 'About' },
  { field: 'connections', label: 'Connections' },
  { field: 'hasEmail', label: 'Has email' },
  { field: 'hasPhone', label: 'Has phone' },
  { field: 'estimatedAge', label: 'Estimated age' },
] as const;

export const SIFT_OPERATOR_LABELS_PURE: Record<string, string> = {
  equals: 'equals',
  not_equals: 'does not equal',
  contains: 'contains',
  not_contains: 'does not contain',
  starts_with: 'starts with',
  ends_with: 'ends with',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
  in_list: 'is in list',
  not_in_list: 'is not in list',
  greater_than: 'greater than',
  less_than: 'less than',
  greater_than_or_equals: 'is at least',
  less_than_or_equals: 'is at most',
};

/** Operators that don't take a value (so the display omits the value part). */
export const SIFT_OPERATORS_WITHOUT_VALUE_PURE: ReadonlySet<string> = new Set(['is_empty', 'is_not_empty']);
